import { useEffect, useRef, useCallback } from 'react';
import { useFetchUserBookings } from './useFetchUserBookings';

/**
 * Enhanced booking hook with automatic refresh capabilities
 * @param {string} userID - The user ID
 * @param {Object} options - Configuration options
 * @param {number} options.pollInterval - Polling interval in milliseconds (default: 30000 = 30 seconds)
 * @param {boolean} options.enablePolling - Whether to enable polling (default: true)
 * @param {boolean} options.refreshOnFocus - Whether to refresh when window gains focus (default: true)
 * @param {boolean} options.refreshOnVisibilityChange - Whether to refresh when tab becomes visible (default: true)
 */
export const useAutoRefreshBookings = (userID, options = {}) => {
  const {
    pollInterval = 30000, // 30 seconds
    enablePolling = true,
    refreshOnFocus = true,
    refreshOnVisibilityChange = true
  } = options;

  // Get the base booking data and refetch function
  const { bookingStudioData, loading, error, refetch } = useFetchUserBookings(userID);
  
  const pollIntervalRef = useRef(null);
  const lastRefreshTime = useRef(Date.now());

  // Enhanced refetch function with smart background refresh
  const throttledRefetch = useCallback(async (silent = true) => {
    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshTime.current;
    
    // Throttle refetches to minimum 5 seconds apart
    if (timeSinceLastRefresh >= 5000) {
      lastRefreshTime.current = now;
      
      if (silent) {
        // Silent background refresh - update cache without loading state
        try {
          await refetch();
        } catch (error) {
          console.error("Background refresh error:", error);
        }
      } else {
        // Normal refetch for user-triggered actions
        refetch();
      }
    }
  }, [refetch]);

  // Set up polling
  useEffect(() => {
    if (!enablePolling || !userID) return;

    const startPolling = () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      
      pollIntervalRef.current = setInterval(() => {
        // Only poll if the document is visible to save resources
        if (!document.hidden) {
          throttledRefetch(true); // Silent background refresh
        }
      }, pollInterval);
    };

    startPolling();

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [userID, pollInterval, enablePolling, throttledRefetch]);

  // Handle window focus events
  useEffect(() => {
    if (!refreshOnFocus) return;

    const handleFocus = () => {
      throttledRefetch(true); // Silent background refresh
    };

    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [refreshOnFocus, throttledRefetch]);

  // Handle page visibility changes
  useEffect(() => {
    if (!refreshOnVisibilityChange) return;

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Tab became visible, silent background refresh
        throttledRefetch(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshOnVisibilityChange, throttledRefetch]);

  // Manual refresh function for external use (user-triggered, show normal loading)
  const manualRefresh = useCallback(() => {
    throttledRefetch(false);
  }, [throttledRefetch]);

  return {
    bookingStudioData,
    loading,
    error,
    refetch: manualRefresh,
    isAutoRefreshEnabled: enablePolling
  };
};

export default useAutoRefreshBookings;
